/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/forbid-elements */
// these rules are disabled bc the <a><img/></a> is uniquely styled compared to
// dibs-elements/Link and should always be an <a/>, even there is no href

import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import { Carousel } from 'dibs-carousel';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';

import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import ResponsiveImage from '../ResponsiveImage';
import HpRespHeroBannerText from '../HpRespHeroBannerText/HpRespHeroBannerText';
import { useUID } from 'dibs-uid';
import classnames from 'classnames';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';

import styles from './HpRespTopHeroBannerModule.scss';

export const HpRespTopHeroBannerModuleComponent = ({
    componentModule,
    useLazyLoadImages,
    isMobile,
    totalModules,
    moduleIndex,
}) => {
    const uid = `topHeroBanner-${useUID() || undefined}`;

    if (!componentModule) {
        return null;
    }

    const items = componentModule.messages?.items || [];
    const isFullBleedImage = !!items.find(item => item.isFullBleedImage);

    const itemsPerPage = 1;

    const onItemsImpression = ({ visibleItems, index }) => {
        const promoTrackingObjects = visibleItems.map(item => {
            return buildPromoTrackingObject({
                id: 'banner-carousel',
                name: 'Banner Carousel',
                item,
                index,
            });
        });
        trackPromoImpressions(promoTrackingObjects);
    };

    const handleClick = (item, index) => {
        const promoTrackingObject = buildPromoTrackingObject({
            id: 'banner-carousel',
            name: 'Banner Carousel',
            item,
            index,
        });
        trackPromoClick(promoTrackingObject);
        trackModuleLocation({
            label: 'banner carousel module',
            moduleIndex,
            totalModules,
        });
    };

    return (
        <div
            className={classnames(styles.carouselContainer, {
                [styles.isFullBleed]: isFullBleedImage,
            })}
        >
            <HpRespModuleContainer>
                <HpRespCarouselTracking
                    items={items}
                    itemsPerPage={itemsPerPage}
                    onItemsImpression={onItemsImpression}
                >
                    {({ handleIndexChange }) => (
                        <SharedInViewport>
                            {({ inViewport }) => (
                                <Carousel
                                    classNames={{
                                        wrapper: styles.carouselWrapper,
                                        item: styles.carouselItem,
                                    }}
                                    isAutoRun={inViewport && items.length > 1}
                                    dataTn="hero-items-carousel"
                                    showDots={items.length > 1}
                                    hideArrows
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index, isVisible }) => {
                                        const item = items[index] || {};
                                        const {
                                            cmsId,
                                            linkData,
                                            imageUrl,
                                            mobileImageUrl,
                                            cta,
                                            imageHeight,
                                            imageWidth,
                                            mobileImageHeight,
                                            mobileImageWidth,
                                            heroBannerSettings,
                                            imageCopy,
                                            format,
                                        } = item;

                                        const itemId = `${uid}-${index}`;

                                        return (
                                            <>
                                                {imageCopy && (
                                                    <span id={itemId} className={styles.imageCopy}>
                                                        {imageCopy}
                                                    </span>
                                                )}
                                                {!isMobile && format !== 'BURN_IN' && (
                                                    <HpRespHeroBannerText
                                                        heroBannerSettings={heroBannerSettings}
                                                        linkData={linkData}
                                                        cta={cta}
                                                    />
                                                )}
                                                <a
                                                    key={cmsId}
                                                    aria-label={cta}
                                                    aria-describedby={
                                                        imageCopy ? itemId : undefined
                                                    }
                                                    href={linkData?.path}
                                                    className={styles.bannerImgContainer}
                                                    style={{ opacity: isVisible ? '1' : '0' }}
                                                    onClick={() => handleClick(item, index)}
                                                >
                                                    <HpLazyImageWrapper
                                                        useLazyLoad={useLazyLoadImages}
                                                        imageHeight={
                                                            mobileImageHeight || imageHeight
                                                        }
                                                        imageWidth={mobileImageWidth || imageWidth}
                                                    >
                                                        <ResponsiveImage
                                                            // do not hide image if there is no copy bc automated a11y testing won't
                                                            // be able to detect this issue
                                                            ariaHidden={
                                                                typeof imageCopy === 'string' &&
                                                                imageCopy !== ''
                                                            }
                                                            className={classnames(
                                                                styles.bannerImg,
                                                                {
                                                                    [styles.isFullBleed]: isFullBleedImage,
                                                                }
                                                            )}
                                                            src={imageUrl || mobileImageUrl}
                                                        />
                                                    </HpLazyImageWrapper>
                                                </a>
                                                {isMobile && format !== 'BURN_IN' && (
                                                    <HpRespHeroBannerText
                                                        heroBannerSettings={heroBannerSettings}
                                                        linkData={linkData}
                                                        cta={cta}
                                                    />
                                                )}
                                            </>
                                        );
                                    }}
                                    onIndexChange={handleIndexChange}
                                    renderDot={({ isCurrentDot } = {}) => (
                                        <SharedCarouselDot isActive={isCurrentDot} />
                                    )}
                                />
                            )}
                        </SharedInViewport>
                    )}
                </HpRespCarouselTracking>
            </HpRespModuleContainer>
        </div>
    );
};

HpRespTopHeroBannerModuleComponent.propTypes = {
    componentModule: PropTypes.object,
    useLazyLoadImages: PropTypes.bool,
    pageSetOffset: PropTypes.bool,
    isMobile: PropTypes.bool,
    totalModules: PropTypes.number,
    moduleIndex: PropTypes.number,
};

export const HpRespTopHeroBannerModule = createFragmentContainer(
    HpRespTopHeroBannerModuleComponent,
    {
        componentModule: graphql`
            fragment HpRespTopHeroBannerModule_componentModule on TopHeroBannerModule {
                messages(userType: $userType) {
                    items {
                        cmsId
                        ... on TopHeroBannerItem @skip(if: $isMobile) {
                            imageUrl
                            imageHeight
                            imageWidth
                        }
                        ... on TopHeroBannerItem @include(if: $isMobile) {
                            mobileImageHeight
                            mobileImageWidth
                            mobileImageUrl
                        }
                        linkData {
                            path
                            ...HpRespHeroBannerText_linkData
                        }
                        heroBannerSettings {
                            ...HpRespHeroBannerText_heroBannerSettings
                        }
                        cta
                        isFullBleedImage
                        imageCopy
                        format
                    }
                }
            }
        `,
    }
);
